import { forwardRef, InputHTMLAttributes, ReactNode, useState } from 'react';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string;
  leadingIcon?: ReactNode;
}

export const TextField = forwardRef<HTMLInputElement, FieldProps>(function TextField(
  { label, hint, error, leadingIcon, className, id, ...rest },
  ref,
) {
  const inputId = id || `f-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div className="space-y-1.5">
      <label htmlFor={inputId} className="block text-sm font-medium">
        {label}
      </label>
      <div className="relative">
        {leadingIcon && (
          <span className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground">
            {leadingIcon}
          </span>
        )}
        <input
          id={inputId}
          ref={ref}
          {...rest}
          className={`w-full h-11 rounded-xl border bg-card px-3 ${leadingIcon ? 'ps-10' : ''} text-sm transition outline-none placeholder:text-muted-foreground/60 focus:ring-2 focus:ring-primary/30 focus:border-primary ${
            error ? 'border-destructive/60' : 'border-border'
          } ${className || ''}`}
        />
      </div>
      {error ? (
        <p className="flex items-center gap-1.5 text-xs text-destructive">
          <AlertCircle className="h-3.5 w-3.5" />
          {error}
        </p>
      ) : (
        hint && <p className="text-xs text-muted-foreground">{hint}</p>
      )}
    </div>
  );
});

interface PasswordFieldProps extends FieldProps {
  showStrength?: boolean;
}

export function PasswordField({ showStrength, ...rest }: PasswordFieldProps) {
  const [shown, setShown] = useState(false);
  return (
    <div className="space-y-1.5">
      <label htmlFor={rest.id || 'password'} className="block text-sm font-medium">
        {rest.label}
      </label>
      <div className="relative">
        {rest.leadingIcon && (
          <span className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground">
            {rest.leadingIcon}
          </span>
        )}
        <input
          id={rest.id || 'password'}
          name={rest.name}
          value={rest.value}
          onChange={rest.onChange}
          onBlur={rest.onBlur}
          autoComplete={rest.autoComplete}
          required={rest.required}
          placeholder={rest.placeholder}
          type={shown ? 'text' : 'password'}
          className={`w-full h-11 rounded-xl border bg-card px-3 ${rest.leadingIcon ? 'ps-10' : ''} pe-10 text-sm transition outline-none placeholder:text-muted-foreground/60 focus:ring-2 focus:ring-primary/30 focus:border-primary ${
            rest.error ? 'border-destructive/60' : 'border-border'
          }`}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setShown((s) => !s)}
          className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          aria-label={shown ? 'Hide password' : 'Show password'}
        >
          {shown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>

      {showStrength && typeof rest.value === 'string' && rest.value.length > 0 && (
        <PasswordStrength value={rest.value} />
      )}

      {rest.error ? (
        <p className="flex items-center gap-1.5 text-xs text-destructive">
          <AlertCircle className="h-3.5 w-3.5" />
          {rest.error}
        </p>
      ) : (
        rest.hint && <p className="text-xs text-muted-foreground">{rest.hint}</p>
      )}
    </div>
  );
}

/**
 * Heuristic password-strength meter. Not crypto-grade — just a UX
 * nudge to encourage longer, more varied passwords. Score 0-4.
 */
function scorePassword(v: string): number {
  let s = 0;
  if (v.length >= 8) s++;
  if (v.length >= 12) s++;
  if (/[A-Z]/.test(v) && /[a-z]/.test(v)) s++;
  if (/\d/.test(v)) s++;
  if (/[^A-Za-z0-9]/.test(v)) s++;
  return Math.min(s, 4);
}

function PasswordStrength({ value }: { value: string }) {
  const s = scorePassword(value);
  const labels = ['Too short', 'Weak', 'OK', 'Strong', 'Very strong'];
  const colors = ['bg-rose-500', 'bg-rose-500', 'bg-amber-500', 'bg-emerald-500', 'bg-emerald-600'];
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 grid grid-cols-4 gap-1">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={`h-1 rounded-full transition ${i < s ? colors[s] : 'bg-muted'}`}
          />
        ))}
      </div>
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{labels[s]}</span>
    </div>
  );
}

export function OrDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="h-px flex-1 bg-border" />
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground whitespace-nowrap">{label}</span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

export function FormError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div role="alert" className="rounded-xl border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-sm text-destructive">
      <div className="flex items-start gap-2">
        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
        <span>{message}</span>
      </div>
    </div>
  );
}

export function SubmitButton({
  loading, children, disabled,
}: { loading?: boolean; children: ReactNode; disabled?: boolean }) {
  return (
    <button
      type="submit"
      disabled={disabled || loading}
      className="gradient-primary inline-flex w-full h-11 items-center justify-center rounded-xl text-white text-sm font-semibold shadow-glow hover:shadow-glow-pink hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60 disabled:pointer-events-none transition"
    >
      {loading ? (
        <span className="inline-flex items-center gap-2">
          <span className="h-3.5 w-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
          {children}
        </span>
      ) : (
        children
      )}
    </button>
  );
}
